import {
  PrimaryButton,
  SecondaryButton,
  DropDown,
  useBreakpointMap,
  BreakpointsOfTOrT,
} from '@grays-npm/component-library';
import TagManager from 'react-gtm-module';
import {
  ListingModalTypeEnum,
  SetModalContentType,
} from '../../pages/Listings/Modals';
import { AcceptBidAction, ListingAction } from './types/ListingAction';

const defaultSize: BreakpointsOfTOrT<'small' | 'large'> = {
  xs: 'small',
  md: 'large',
};

export type ListingActionButtonsProps = {
  actions: ListingAction[];
  onPromote: VoidFunction;
  onReview: VoidFunction;
  onLowerReserve: SetModalContentType<ListingModalTypeEnum.LowerReserve>;
  onHoldReserve: SetModalContentType<ListingModalTypeEnum.HoldReserve>;
  onAcceptBid: SetModalContentType<ListingModalTypeEnum.AcceptBid>;
};

export const ListingActionButtons = ({
  actions,
  onLowerReserve,
  onHoldReserve,
  onAcceptBid,
}: ListingActionButtonsProps) => {
  const onClickAction = (action: ListingAction) => {
    switch (action.type) {
      case 'lower-reserve-referred':
        return () => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'activeListings-referredItemLowerReserve',
            },
          });
          onLowerReserve({
            type: ListingModalTypeEnum.LowerReserve,
            data: {
              ...action.data,
              listingType: 'referred',
              reserve: action.data.currentReserve,
              bid: action.data.bid,
              remainingActionCount: action.data.remainingActionCount,
            },
          });
        };
      case 'lower-reserve-auction':
        return () => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'activeListings-auctionItemLowerReserve',
            },
          });
          onLowerReserve({
            type: ListingModalTypeEnum.LowerReserve,
            data: {
              ...action.data,
              listingType: 'auction',
              reserve: action.data.currentReserve,
              bid: action.data.bid,
            },
          });
        };
      case 'hold-reserve':
        const secondaryAction = actions.find((a) => a.type === 'accept-bid') as
          | AcceptBidAction<'accept-bid'>
          | undefined;
        return () => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'activeListings-referredItemHoldReserve',
            },
          });
          onHoldReserve({
            type: ListingModalTypeEnum.HoldReserve,
            data: action.data,
            ...(secondaryAction && {
              secondaryData: secondaryAction.data,
            }),
          });
        };
      case 'accept-bid':
        return () => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'activeListings-referredItemAcceptBid',
            },
          });
          onAcceptBid({
            type: ListingModalTypeEnum.AcceptBid,
            data: action.data,
          });
        };
      /**
       * * remaining actions will include:
       * - accept-offer
       * - reject-offer
       * - promote
       * - review
       */
      default:
        return () => {};
    }
  };

  const mappedSize = useBreakpointMap(defaultSize);

  if (mappedSize === 'small' && actions.length >= 3) {
    const [firstAction, ...otherActions] = actions;
    const ButtonType = firstAction.isPrimary ? PrimaryButton : SecondaryButton;

    return (
      <>
        <ButtonType
          key={firstAction.type}
          size={{ xs: 'small', sm: 'medium' }}
          onClick={onClickAction(firstAction)}
        >
          {firstAction.title}
        </ButtonType>
        <DropDown
          onChange={(option) => {
            const selectedAction = otherActions.find(
              (action) => action.type === option
            );
            selectedAction && onClickAction(selectedAction)();
          }}
          options={otherActions.map((action) => ({
            label: action.title,
            value: action.type,
          }))}
          overrideOption={{ label: 'More', value: 'default' }}
          dropDownButtonType="secondary"
          buttonSize={{ xs: 'small', sm: 'medium' }}
          selectedValue="default"
          fullWidth
        />
      </>
    );
  }

  return (
    <>
      {actions.map((action) => {
        const ButtonType = action.isPrimary ? PrimaryButton : SecondaryButton;

        return (
          <ButtonType
            key={action.type}
            size={{ xs: 'small', sm: 'medium' }}
            onClick={onClickAction(action)}
          >
            {action.title}
          </ButtonType>
        );
      })}
    </>
  );
};
